import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { catchError, Observable, of, retry, tap, throwError, timer } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  FunctionalExceptions,
  SystemExceptions,
  TreatableFunctionalExceptions,
  TreatableSystemExceptions,
} from '../../shared/enums/exceptions.enum';
import { StorageKeys } from '../../shared/enums/storage-keys.enum';
import { UrlKeys } from '../../shared/enums/urls.enum';
import { UserData } from '../../shared/models/user-data';
import { ApiPrivateService } from '../../shared/services/api-private.service';
import { AuthService } from '../../shared/services/auth.service';
import { ConsoleService } from '../../shared/services/console.service';
import { CookiesService } from '../../shared/services/cookies.service';
import { MessageService } from '../../shared/services/message.service';
import { StorageService } from '../../shared/services/storage.service';
import { UtilsService } from '../../shared/services/utils.service';

export const generalInterceptor: HttpInterceptorFn = (req, next) => {
  const _apiPrivateService = inject(ApiPrivateService);
  const _authService = inject(AuthService);
  const _consoleService = inject(ConsoleService);
  const _cookiesService = inject(CookiesService);
  const _dynamicDialogRef = inject(DynamicDialogRef);
  const _messageService = inject(MessageService);
  const _storageService = inject(StorageService);
  const _utilsService = inject(UtilsService);
  const _router = inject(Router);

  const MAX_RETRIES = 3;
  const RETRY_DELAY = 150;
  const isApp = _utilsService.isApp();

  const api = _apiPrivateService.apiURLs;
  const routerUrl = _router.url;
  const url = req.url;
  const requestPath = url.split('?')[0];
  const isPrivate = url.includes('/private');
  const isCitasListRequest = req.method === 'GET' && requestPath === api.cita.citas;
  const isCitasListOnHome = isCitasListRequest && routerUrl.includes(UrlKeys.HOME);
  const isLogoutRequest = requestPath === api.audit.logout;
  const isKeepAlive = url.includes(api.home.keepAlive);
  const isI18n = url.indexOf('/i18n/') !== -1;
  const isDownloadHelptext = url.indexOf('messages/helptext/download') !== -1;

  if (!!environment.baseURLBack && url.indexOf('/api') === 0) {
    _consoleService.log('[interceptor]', url, environment.baseURLBack + url);
    req = req.clone({ url: environment.baseURLBack + url });
  }

  if (
    isPrivate &&
    !(
      url.includes(api.home.home) ||
      url.includes(api.user.misDatos) ||
      url.includes(api.home.authorized)
    )
  ) {
    const userData: UserData = _storageService.sessionStorageGetItem(StorageKeys.LOGIN_DATA);
    const userCookieValue =
      _cookiesService.get('_uIn' + btoa(userData.insuranceNumber)) ||
      _cookiesService.get('_uIn' + btoa(userData.fullName));
    if (!userCookieValue && !isApp) {
      _consoleService.log('[interceptor] noUserCookie', req);
      if (url !== api.audit.logout) {
        _authService.logOut();
      }
      return throwError(() => 'logout');
    }
  }

  return next(req).pipe(
    tap(event => {
      if (event instanceof HttpResponse) {
        const auth: string | null = event.headers.get('Customer');
        if (!!auth && !!auth.split('Bearer ')[1]) {
          _storageService.sessionStorageSetItem(StorageKeys.LOGIN_TOKEN, auth.split('Bearer ')[1]);
        }
      }
    }),
    retry({
      count: MAX_RETRIES,
      delay: (error: HttpErrorResponse, retryAttempt: number): Observable<number> => {
        if (!isI18n) {
          return throwError(() => error);
        }

        const backoff = RETRY_DELAY * Math.pow(2, retryAttempt - 1);
        const jitter = Math.floor(Math.random() * 50);

        _consoleService.log(`[interceptor] retry in ${backoff + jitter}ms`, url.indexOf('/i18n/'));

        return timer(backoff + jitter);
      },
    }),
    catchError(error => {
      if (error.status === 200 && isDownloadHelptext) {
        return of(new HttpResponse({ body: error.error }));
      }

      _consoleService.log('[interceptor] error', error);
      if (isCitasListOnHome) {
        return of(new HttpResponse({ body: error.error }));
      }
      if (error.status === 500) {
        if (
          error?.error?.systemExceptions[0].codError === 'TokenInvalid' &&
          error?.error?.systemExceptions[0].typeError === 'Unauthorized' &&
          error?.error?.systemExceptions[0].descError === 'Token is invalid or missing'
        ) {
          if (!isLogoutRequest) {
            _authService.logOut();
          }
          return throwError(() => error);
        } else if (!url.includes(api.home.keepAlive)) {
          if (!isKeepAlive) return of();
        } else {
          _messageService.showMessage('error', 'back_errors.500');
          _dynamicDialogRef.close();
          return throwError(() => error);
        }
      } else if (error.status === 401 && !url.includes(api.audit.logout)) {
        _authService.logOut();
        return throwError(() => error);
      }

      if (
        error?.error?.functionalExceptions?.length &&
        !treatableFunctionalException(error?.error?.functionalExceptions[0])
      ) {
        _messageService.showMessage(
          'error',
          functionalExceptionMessageText(error?.error?.functionalExceptions[0])
        );
        return of();
      } else if (
        error?.error?.systemExceptions?.length &&
        !treatableSystemException(error?.error?.systemExceptions[0])
      ) {
        _messageService.showMessage(
          'error',
          systemExceptionMessageText(error?.error?.systemExceptions[0])
        );
        return of();
      }

      if (error.error instanceof Blob) {
        error.error.text().then((blobText: any) => {
          if (blobText.includes('functionalExceptions')) {
            const excep: any = JSON.parse(blobText);
            _messageService.showMessage(
              'error',
              functionalExceptionMessageText(excep?.functionalExceptions[0])
            );
          }
        });
        return throwError(() => 'error');
      }

      return of(new HttpResponse({ body: error.error }));
    })
  );
};

function treatableFunctionalException(excp: any): boolean {
  return !!TreatableFunctionalExceptions.find(
    item => item.typeError === excp.typeError && item.descError === excp.descError
  );
}

function functionalExceptionMessageText(excp: any): string {
  return (
    FunctionalExceptions.find(
      item => item.typeError === excp.typeError && item.descError === excp.descError
    )?.errorText || excp.descError
  );
}

function treatableSystemException(excp: any): boolean {
  return !!TreatableSystemExceptions.find(
    item => item.typeError === excp.typeError && item.descError === excp.descError
  );
}

function systemExceptionMessageText(excp: any): string {
  return (
    SystemExceptions.find(
      item => item.typeError === excp.typeError && item.descError === excp.descError
    )?.errorText || excp.descError
  );
}
